import { ImageSourcePropType, Pressable } from 'react-native';
import { Platform, View } from 'react-native';
import { Image } from 'expo-image';
import * as AppConstants from '@/utilities/AppConstants';
import * as AppConsts from '@/utilities/AppConsts';
import * as AppUtilities from '@/utilities/AppUtilities';
import { Community, UNKNOWN_COMMUNITY } from "@/types/community";
import { CommunityMembership, UNKNOWN_COMMUNITY_MEMBERSHIP, PaymentStatus } from "@/types/community_membership";
import { Profile, UNKNOWN_PROFILE } from "@/types/profile";
import { Gender, UNKNOWN_USER_INFO, UserInfo } from "@/types/user_info";
import { v4 as uuidv4 } from 'uuid';
import { RelativePathString } from 'expo-router';
import {
  finishUpload,
  startUpload,
  removeUpload,
  updateVisuals,
  updateUploadProgress,
  FinishUploadUpdate,
  Visual,
  UploadProgress,
  UploadProgressUpdate,
} from '@/state/uploads';
import * as ImagePicker from 'expo-image-picker';
import {launchCamera, launchImageLibrary, Asset} from 'react-native-image-picker';
import * as FileSystem from 'expo-file-system/legacy';
import axios from 'axios';
import { ActionCreatorWithPayload, Dispatch, UnknownAction } from '@reduxjs/toolkit';
import { Buffer } from 'buffer';
import * as Device from 'expo-device';
import { setLoggedIn } from '@/state/user';
import { Collaboration, CollaborationStatus, UNKNOWN_COLLABORATION } from '@/types/collaboration';
import * as Burnt from "burnt";
import { toast } from '@/components/CustomSonner';
import { Button, Panel } from 'react95-native-rabbl';
import { UAParser } from 'ua-parser-js';
import { BulletItem } from '@/types/BulletItem';
import { differenceInYears } from "date-fns";
import { SetStateAction } from 'react';

export interface GlobalsInterface {
  nextRootRedirect: RelativePathString | undefined,
  iOSMajorVersion?: number,
  iOSMinorVersion?: number,
}

export const Globals : GlobalsInterface = {
  nextRootRedirect: undefined,
};

export function initializeOneTimeFast() {
  try {
    const osVersion = Device.osVersion;
    if (osVersion) {
      const osVersionPieces = osVersion.split(".");
      if (osVersionPieces.length > 0) {
        Globals.iOSMajorVersion = parseInt(osVersionPieces[0]);
        if (osVersionPieces.length > 1) {
          Globals.iOSMinorVersion = parseInt(osVersionPieces[1]);
        }
      }
    }
  } catch (e) {
    AppUtilities.handleError(e);
  }
  if (AppUtilities.DEBUGLEVEL.dev) {
    AppUtilities.info("AppUtils Globals:");
    AppUtilities.dir(Globals);
  }
}

export function formatPublicPrice(amount: number, includeCurrencySymbol: boolean = true): string {
  if (Math.round(amount) == amount) {
    return formatCurrency(amount - 0.01, includeCurrencySymbol);
  } else {
    return formatCurrency(amount, includeCurrencySymbol);
  }
}

export function formatCurrency(amount: number, includeCurrencySymbol: boolean = true) : string {
  let minimumFractionDigits = 2;
  if (Math.floor(amount) == amount) {
    minimumFractionDigits = 0;
  }

  return new Intl.NumberFormat("en-US", {
    style: includeCurrencySymbol ? "currency" : "decimal",
    currency: "USD",
    minimumFractionDigits: minimumFractionDigits,
    maximumFractionDigits: 2,
  }).format(amount);
}

export function isDevelopment() : boolean {
  return process.env.NODE_ENV === 'development';
}

export function isAccessTokenExpired() : boolean {
  return AppUtilities.isAccessTokenExpired() as boolean;
}

export function isBlank(s: string|undefined|null) : boolean {
  if (s === undefined || s === null) {
    return true;
  } else if (s.length == 0) {
    return true;
  }
  return false;
}

export function isCommunityMembershipFullyPaid(membership: CommunityMembership) : boolean {
  return membership.payment_status == PaymentStatus.PAYMENT_STATUS_SUCCESSFUL;
}

export function getProfileCreateNextPageLink(membership: CommunityMembership) : string {
  let result = '/';
  if (membership.uniqueid == UNKNOWN_COMMUNITY_MEMBERSHIP.uniqueid) {
    result = '/';
  } else if (!isCommunityMembershipFullyPaid(membership)) {
    result = '/pay';
  } else if (!membership.user_info.has_email || isBlank(membership.user_info.email)) {
    result = '/setlogin';
  } else if (!membership.user_info.set_password) {
    result = '/password';
  } else if (!membership.profile) {
    result = '/pickprofile';
  } else if (isBlank(membership.profile.name) || membership.profile.name.startsWith('tmp')) {
    result = '/name';
  } else if (membership.user_info.age_year <= 0) {
    result = '/age';
  } else if (membership.user_info.height == -1) {
    result = '/height';
  } else if (membership.profile.visuals.length == 0) {
    result = '/visuals';
  } else if (isBlank(membership.user_info.location_name) || membership.user_info.location_latitude == 0 || membership.user_info.location_longitude == 0) {
    result = '/location';
  } else if (isBlank(membership.profile.description)) {
    result = '/aboutyou';
  } else if (!membership.user_info.desire_men && !membership.user_info.desire_women) {
    result = '/desires';
  } else if (!membership.user_info.desired_age_min || membership.user_info.desired_age_min <= 0) {
    result = '/desired_ages';
  } else if (!membership.user_info.set_preferences) {
    result = '/preferences';
  } else if (!membership.user_info.accepted_terms) {
    result = '/userinfo';
  } else {
    // Pending https://github.com/expo/expo/issues/42727
    result = AppConsts.SUBDOMAIN_HOMEPAGE_LOGGED_IN;
  }
  AppUtilities.info("AppUtils.getProfileCreateNextPageLink = " + result);
  return result;
}

export const WAITING_APPROVAL_LINK = '/waitingapproval';

export function getRabblCreateNextPageLink(community: Community, userInfo: UserInfo) : string {
  if (community.id == UNKNOWN_COMMUNITY.id) {
    return '/';
  }

  if (!community.name || community.name.length == 0) {
    return '/rabblname';
  } else if (!community.price_men || community.price_men < 1 || !community.price_women || community.price_women < 1) {
    return '/covercharge';
  } else if (community.main_visual <= 0) {
    return '/rabblvisuals';
  } else if (!community.details || community.details.length == 0) {
    return '/rabbldetails';
  } else if (!community.hasBackground) {
    return '/rabblbackground';
  } else if (!community.domain || community.domain.length == 0) {
    return '/rabbllink';
  } else if (!userInfo.accepted_terms) {
    return '/user_info';
  } else if (!community.approved) {
    return WAITING_APPROVAL_LINK;
  } else {
    return '/';
  }
}

export function getUUID() : string {
  return uuidv4();
}

export function isSmallScreen(width: number): boolean {
  return width < 500;
}

/**
 * Get an absolute link to the backend server.
 * 
 * @param relativeUri Relative URI starting with /, e.g. /rabbl/api/...
 * @returns 
 */
export function createBackendLink(relativeUri: string): string {
  let result = AppUtilities.getActualHostLink(false, false) + relativeUri;
  result = AppUtilities.addCommonQueryParameters(result);
  if (AppUtilities.DEBUGLEVEL.dev) {
    //AppUtilities.info("AppUtils.createBackendLink: " + result);
  }
  return result;
}

/**
 * Get an absolute link to the frontend server.
 * 
 * @param relativeUri Relative URI starting with /, e.g. /rabbl/api/...
 * @returns 
 */
export function createFrontendLink(relativeUri: string): string {
  let result = AppUtilities.getActualHostLink(false, false) + relativeUri;
  result = AppUtilities.addCommonQueryParameters(result);
  //AppUtilities.info("AppUtils.createFrontendLink: " + result);
  return result;
}

/**
 * 
 * @param community 
 * @returns There is no ending /
 */
export function getCommunityURL(community: Community) : string {
  let result = AppUtilities.getActualHostLink(true, true);
  result = result.replace('://', '://' + community.domain + '.');
  return result;
}

export function getCommunityImageURL(community: Community) : string {
  return createBackendLink('/rabbl/api/communityvisual?thumbnail=yes&community_id=' + community.id + '&id=' + community.main_visual);
}

export function getCommunityImage(community: Community) : ImageSourcePropType {
  if (community.main_visual > 0) {
    return {
      uri: createBackendLink('/rabbl/api/communityvisual?thumbnail=yes&community_id=' + community.id + '&id=' + community.main_visual),
    };
  } else {
    return require('@/assets/images/butterfly325x325.png');
  }
}

export function isCommunityFree(community: Community, gender: Gender) : boolean {

  // While we wait for app store approval
  if (isIOSFree() && AppUtilities.isIOS()) {
    return true;
  }

  if (AppUtilities.DEBUGLEVEL.dev) {
    //AppUtilities.info("AppUtils.isCommunityFree: gender: " + gender + ", first_free_men: " + community.first_free_men + ", members_men" + community.members_men);
  }

  if ((gender == Gender.GENDER_MAN) && community.first_free_men > 0 && community.members_men < community.first_free_men) {
    return true;
  }

  if (AppUtilities.DEBUGLEVEL.dev) {
    //AppUtilities.info("AppUtils.isCommunityFree: gender: " + gender + ", first_free_women: " + community.first_free_women + ", members_women" + community.members_women);
  }

  if ((gender == Gender.GENDER_WOMAN) && community.first_free_women > 0 && community.members_women < community.first_free_women) {
    return true;
  }

  return false;
}

export function getFreeCommunityPromotionText(community: Community) : string {
  if (community.first_free_men > 0 && community.first_free_women > 0) {
    return "for the first " + community.first_free_men + " men & " + community.first_free_women + " women";
  } else if (community.first_free_men > 0) {
    return "for the first " + community.first_free_men + " men";
  } else if (community.first_free_women > 0) {
    return "for the first " + community.first_free_women + " women";
  } else if (isIOSFree() && AppUtilities.isIOS()) {
    return " (promotional)";
  }
  return "";
}

export function isIOSFree() {
  return true;
}

export function getMemberAge(member: CommunityMembership) : number {
  return getMemberAgeByUserInfo(member.user_info);
}

export function getMemberAgeByUserInfo(user_info: UserInfo) : number {
  return user_info.age_years;
}

export function getMemberShortRegion(user_info: UserInfo) : string {
  let result = user_info.location_name;
  if (result && result.length > 0) {
    if (result.endsWith(", USA")) {
      result = result.substring(0, result.length - 5);
    }
    const i = result.lastIndexOf(",");
    if (i != -1) {
      result = result.substring(i + 1).trim();
    }
    if (result.length <= 3) {
      return result;
    }
  }
  return "";
}

const STATE_ZIP_REGEX = /^(.*, [A-Z]+),? [0-9]+$/;

export function getMemberShortLocation(user_info: UserInfo) : string {
  let result = user_info.location_name;
  if (result && result.length > 0) {
    if (result.endsWith(", USA")) {
      result = result.substring(0, result.length - 5);
    }
    const match = result.match(STATE_ZIP_REGEX);
    if (match) {
      result = match[1];
    }
  }
  return result;
}

export function prefixIfNonEmpty(str: string, prefix: string) : string {
  return str == undefined || str == null || str.length == 0 ? str : prefix + str;
}

export function getHeightHumanReadable(height: number, fixedWidth: boolean = false): string {
  const inches = height % 12;
  const feet = Math.floor(height / 12);
  if (inches == 0 && !fixedWidth) {
    return feet + " ft";
  } else {
    return feet + " ft " + inches + " in" + (fixedWidth && inches < 10 ? " " : "");
  }
}

export function showInformationalToast(
  message: string,
  subheading: string | undefined = undefined,
  type: "done" | "error" | "none" = "none",
) {
  if (AppUtilities.isWeb()) {
    toast(
      message,
      {
        description: subheading,
        closeButton: true,
        position: 'bottom-center',
      },
    );
  } else {
    // https://github.com/nandorojo/burnt#toast
    Burnt.toast({
      title: message,
      preset: type,
      message: subheading,
    });
  }
}

export function showPressableToast(
  message: string,
  onPress: () => void,
  pressLabel: string,
  subheading: string | undefined = undefined,
) {
  // https://github.com/gunnartorfis/sonner-native-toasts
  // https://sonner-native.netlify.app/toast
  // https://sonner-native.netlify.app/toast#api-reference
  toast(
    message,
    {
      action: {
        label: pressLabel,
        onClick: onPress,
      },
      description: subheading,
      closeButton: true,
      position: 'bottom-center',
    },
  );
}

export function showErrorToast(message: string, retry: (() => void) | null = null, permanent: boolean = true, retryText: string = "Retry") {
  if (retry) {
    toast.error(
      message,
      {
        action: {
          label: retryText,
          onClick: retry,
        },
        description: undefined,
        closeButton: true,
        position: 'bottom-center',
        duration: 10000,
      },
    );
  } else {
    toast.error(
      message,
      {
        description: undefined,
        closeButton: true,
        position: 'bottom-center',
        duration: 10000,
      },
    );
  }
}

export function showDialog(
  message: string,
  onPressOk: () => void = () => {},
  okText: string = "OK",
  cancelText: string = "Cancel"
) {
  toast(
    message,
    {
      action: {
        label: okText,
        onClick: onPressOk,
      },
      cancel: {
        label: cancelText,
        onClick: () => {},
      },
      description: undefined,
      closeButton: true,
      position: 'bottom-center',
      duration: Infinity,
    },
  );
}

export function getHomeLink(loggedIn: boolean, mainLogicalhost: boolean, communityMembership: CommunityMembership) : RelativePathString {
  const hasCommunityMembership = (communityMembership != null && communityMembership.uniqueid != UNKNOWN_COMMUNITY_MEMBERSHIP.uniqueid);
  let result: RelativePathString;
  if (!mainLogicalhost) {
    if (loggedIn) {
      if (hasCommunityMembership) {
        result = AppConsts.SUBDOMAIN_HOMEPAGE_LOGGED_IN as RelativePathString;
      } else {
        result = AppConsts.SUBDOMAIN_HOMEPAGE_LOGGED_IN_UNJOINED as RelativePathString;
      }
    } else {
      result = AppConsts.SUBDOMAIN_HOMEPAGE_LOGGED_OUT as RelativePathString;
    }
  } else {
    result = AppConsts.ROUTING_PREFIX + "/(maindomain-loggedinorout-tabs)/" as RelativePathString;
  }
  if (AppUtilities.DEBUGLEVEL.beta) {
    //AppUtilities.info("AppUtils.getHomeLink for loggedIn: " + loggedIn + ", mainLogicalHost: " + mainLogicalhost + ", hasCommunityMembership: " + hasCommunityMembership + " = " + result);
  }
  return result;
}

function dataUrlToFile(dataUrl: string, filename: string): File {
  const arr = dataUrl.split(',');
  if (arr.length < 2) {
    throw new Error('TODO1');
  }
  const mimeArr = arr[0].match(/:(.*?);/);
  if (!mimeArr || mimeArr.length < 2) {
    throw new Error('TODO2');
  }
  const mime = mimeArr[1];
  const buff = Buffer.from(arr[1], 'base64');
  return new File([buff], filename, { type: mime });
}

export async function handleVisualUpload(
  fileModel: string,
  identity_id: number,
  dispatch: any,
  visual: Visual,
  asset1?: ImagePicker.ImagePickerAsset,
  asset2?: Asset,
) {
  const context = "AppUtils.handleVisualUpload (" + fileModel + ")";
  try {
    if (asset1) {
      AppUtilities.info(context + " asset1");
      AppUtilities.dir(asset1);
    }
    if (asset2) {
      AppUtilities.info(context + " asset2");
      AppUtilities.dir(asset2);
    }

    const uri = AppUtilities.getActualHostLink(false, false) + "/api/newfile2";
    AppUtilities.info(context + " uploading to " + uri);

    const accessTokenResponse = await AppUtilities.ensureValidAccessToken();
    if (accessTokenResponse.successful) {
      AppUtilities.info(context + " access token valid");
      if (!AppUtilities.isWeb()) {

        AppUtilities.info(context + " phone app upload");

        // We can't use axios because of https://github.com/axios/axios/issues/6015#issuecomment-2644033602
        let fileUri: string = '';
        if (asset1) {
          fileUri = asset1.uri;
        } else if (asset2 && asset2.uri) {
          fileUri = asset2.uri;
        } else {
          throw new Error("TODO3: asset2 unhandled");
        }
        const task = FileSystem.createUploadTask(
          uri,
          fileUri,
          {
            fieldName: "identity_file[file]",
            httpMethod: "POST",
            uploadType: FileSystem.FileSystemUploadType.MULTIPART,
            // "If not provided, the module will try to guess it based on the extension."
            // https://docs.expo.dev/versions/latest/sdk/filesystem-legacy/#uploadoptionsmultipart
            //mimeType: mimeType,
            headers: {
              Authorization: "Bearer " + accessTokenResponse.token,
              Accept: "application/json",
            },
            parameters: {
              engine: "Rabbl",
              identity_id: '' + identity_id,
              model: fileModel,
              strip: "1",
            },
          },
          (progressData) => {
            const sent = progressData.totalBytesSent;
            const total = progressData.totalBytesExpectedToSend;
            if (total > 0) {
              const update = {
                visual: visual,
                progress: {
                  uploaded: sent,
                  total: total,
                }
              };
              dispatch(updateUploadProgress(update));
              if (AppUtilities.DEBUGLEVEL.uploads) {
                console.log("onUploadProgress");
                console.dir(update);
              }
            }
          },
        );
        const response = await task.uploadAsync();
        if (response?.status == 200) {
          const responseJSON = JSON.parse(response.body);
          if (responseJSON && responseJSON.id) {
            dispatch(finishUpload({
              visual,
              newId: '' + responseJSON.id,
            }));
          } else {
            const message = "Upload failed with unexpected response data";
            AppUtilities.dir(response);
            AppUtilities.handleError(message, context + "");
            throw new Error(message);
          }
        } else {
          const message = "Upload failed with response " + response?.status;
          AppUtilities.dir(response);
          AppUtilities.handleError(message, context + "");
          throw new Error(message);
        }
      } else {
        const formData = new FormData();
        formData.append('engine', 'Rabbl');
        formData.append('identity_id', '' + identity_id);
        formData.append('model', fileModel);
        formData.append('strip', "1");

        if (asset1) {
          if (asset1.file && (Object.keys(asset1.file).length > 0 || asset1.file instanceof File)) {
            AppUtilities.info(context + " uploading file attribute");
            formData.append('identity_file[file]', asset1?.file as any);
          } else if (asset1.uri && asset1.mimeType && asset1.fileName && asset1.uri.startsWith('blob:')) {
            AppUtilities.info(context + " uploading uri blob");
            formData.append(
              'identity_file[file]',
              {
                uri: asset1.uri,
                name: asset1.fileName,
                type: asset1.mimeType,
              } as any
            );
          } else {
            throw new Error('TODO: asset1 unhandled type');
          }
        } else if (asset2?.uri) {
          let data = asset2?.uri;
          if (data.startsWith('data:') && data.indexOf(';base64,') != -1) {
            AppUtilities.info(context + " found data: with base64");
            const file = dataUrlToFile(data, 'uploadedfile');
            formData.append('identity_file[file]', file);
          } else {
            throw new Error('TODO: asset2 unhandled type');
          }
        }

        const response = await axios.post(
          AppUtilities.addCommonQueryParameters(uri),
          formData,
          {
            headers: {
              Authorization: "Bearer " + accessTokenResponse.token,
              Accept: "application/json",
            },
            onUploadProgress: (progressEvent) => {
              if (progressEvent.total) {
                const update = {
                  visual: visual,
                  progress: {
                    uploaded: progressEvent.loaded,
                    total: progressEvent.total,
                  }
                };
                dispatch(updateUploadProgress(update));
                if (AppUtilities.DEBUGLEVEL.uploads) {
                  console.log("onUploadProgress");
                  console.dir(update);
                }
              }
            },
          }
        );

        if (AppUtilities.DEBUGLEVEL.uploads || AppUtilities.DEBUGLEVEL.dev) {
          console.log("Response:");
          console.dir(response);
        }

        if (response.status == 200) {
          dispatch(finishUpload({
            visual,
            newId: '' + response.data.id,
          }));
        } else {
          const message = "Upload failed with " + response.status + " " + response.statusText;
          AppUtilities.dir(response);
          AppUtilities.handleError(message, context + "");
          throw new Error(message);
        }
      }
    } else {
      throw new Error("Access token could not be refreshed while attempting upload. Please clear your cache, reload and log in again.");
    }

  } catch (e) {
    dispatch(removeUpload(visual));
    AppUtilities.handleException(e, context);
    showErrorToast("Upload failed. Please try again.", null, true);
  }
}

export function isValidCommunity(community: Community) : boolean {
  return community &&
         community.id != UNKNOWN_COMMUNITY.id;
}

export function isValidCommunityMembership(communityMembership: CommunityMembership) : boolean {
  return communityMembership &&
         communityMembership.uniqueid != UNKNOWN_COMMUNITY_MEMBERSHIP.uniqueid;
}

export function isValidProfile(profile: Profile) : boolean {
  return profile &&
         profile.profile_id != UNKNOWN_PROFILE.profile_id;
}

export function isValidUserInfo(user_info: UserInfo) : boolean {
  return user_info &&
         user_info.user_info_id != UNKNOWN_USER_INFO.user_info_id;
}

export function isValidUserInfoGender(user_info: UserInfo) : boolean {
  return user_info &&
         user_info.user_info_id != UNKNOWN_USER_INFO.user_info_id &&
         (user_info.gender == Gender.GENDER_MAN || user_info.gender == Gender.GENDER_WOMAN);
}

export function isValidCollaboration(collaboration: Collaboration) : boolean {
  return collaboration &&
         collaboration.uniqueid != UNKNOWN_COLLABORATION.uniqueid;
}

const simpleGlobalStateTransient = new Map<string, any>();

export function getSimpleGlobalStateTransient<T>(key: string): T | undefined {
  return simpleGlobalStateTransient.get(key);
}

export function setSimpleGlobalStateTransient<T>(key: string, val: T): void {
  simpleGlobalStateTransient.set(key, val);
}

export function closeDrawerActionState(
  callback: () => void,
  hideSpinner: () => void,
  setDrawerOpen: React.Dispatch<React.SetStateAction<boolean>>,
): boolean {
  setDrawerOpen(false);
  if (AppUtilities.isAndroid()) {
    // https://github.com/software-mansion/react-native-screens/issues/3249
    setTimeout(
      () => {
        hideSpinner();
        callback();
      },
      AppConsts.DRAWER_DELAY_MILLISECONDS,
    );
    return false;
  } else {
    callback();
    return true;
  }
}

export function closeDrawerActionRedux(
  callback: () => void,
  hideSpinner: () => void,
  dispatch: Dispatch<UnknownAction>,
  setDrawerOpen: ActionCreatorWithPayload<boolean, "user/setDrawerOpen">,
): boolean {
  dispatch(setDrawerOpen(false));
  if (AppUtilities.isAndroid()) {
    // https://github.com/software-mansion/react-native-screens/issues/3249
    setTimeout(
      () => {
        hideSpinner();
        callback();
      },
      AppConsts.DRAWER_DELAY_MILLISECONDS,
    );
    return false;
  } else {
    callback();
    return true;
  }
}

export async function logout(dispatch: Dispatch<UnknownAction>, showSpinner?: () => void, hideSpinner?: () => void) {

  // Call logout which will return expired cookies that'll override the local cookies.
  // TODO is this needed on non-web? I think it's needed on web because of httponly cookies,
  // but on apps, could we just call CookieManager.clearAll?
  //if (!AppUtilities.isWeb()) {
  //  await CookieManager.clearAll();
  //}
  try {
    if (showSpinner) {
      showSpinner();
    }

    const response = await AppUtilities.fetchUnauthorized(
      "AppUtils.logout",
      "/rabbl/api/logout",
      "POST",
      {
      }
    );
  } catch (e) {
    AppUtilities.handleException(e, "AppUtils.logout");
  } finally {
    if (hideSpinner) {
      hideSpinner();
    }
  }

  AppUtilities.logout();
  dispatch(setLoggedIn(false));
  dispatch(updateVisuals([]));
}

export function parseAndDisplayISO8601DateString(iso8601DateStr: string) : string {
  return new Date(iso8601DateStr).toLocaleDateString();
}

export function getCollaborationOtherCommunity(collaboration: Collaboration, referenceCommunity: Community) : Community {
  if (collaboration.rabbl_community1.uniqueid == referenceCommunity.uniqueid) {
    return collaboration.rabbl_community2;
  } else {
    return collaboration.rabbl_community1;
  }
}

export function getCollaborationStatusDisplay(collaboration: Collaboration) : string {
  if (collaboration.collabstatus == CollaborationStatus.COLLABORATION_STATUS_ACTIVE) {
    return "Active";
  } else if (collaboration.collabstatus == CollaborationStatus.COLLABORATION_STATUS_CANCELED) {
    return "Cancelled";
  } else if (collaboration.collabstatus == CollaborationStatus.COLLABORATION_STATUS_EMAILED) {
    return "Waiting for approval";
  } else if (collaboration.collabstatus == CollaborationStatus.COLLABORATION_STATUS_EXPIRED) {
    return "Expired";
  } else {
    return "Unknown";
  }
}

export function getDistanceDisplay(x : number): string {
  return "" + Math.trunc(x);
}

export function getLifetimeMembershipText(community?: Community) : string {
  if (community) {
    return "Lifetime membership for the life of this " + community.name + " Rabbl" + (AppUtilities.isWeb() ? " website" : "");
  } else {
    return "Lifetime membership for the life of this Rabbl" + (AppUtilities.isWeb() ? " website" : "");
  }
}

export function webFullPageNavigate(url: string) : void {
  //window.location.replace(url);
  window.location.href = url;
}

export enum MaxWidthType {
  DEFAULT = "default",
  INNER = "inner",
  INNER2 = "inner2",
  INNER3 = "inner3",
};

export function calculateMaxWidth(screenWidth: number, maxWidthType: MaxWidthType = MaxWidthType.DEFAULT): number {
  if (maxWidthType == MaxWidthType.INNER) {
    return screenWidth - 40;
  } else if (maxWidthType == MaxWidthType.INNER2) {
    return screenWidth - (AppUtilities.isWeb() ? 50 : 90);
  } else if (maxWidthType == MaxWidthType.INNER3) {
    return screenWidth - (AppUtilities.isWeb() ? 110 : 90);
  } else {
    return screenWidth - 30;
  }
}

export function handleExceptionUnlessParticularStatus(exception: any, source: string, skipStatus: number) : void {
  let shouldReportException = true;
  if (exception.status && exception.status == skipStatus) {
    shouldReportException = false;
  }
  if (shouldReportException) {
    AppUtilities.handleException(exception, source);
  }
}

let LOGICAL_USER_AGENT : UAParser.IResult | null = null;
try {
  LOGICAL_USER_AGENT = UAParser();
} catch (e) {
  //console.dir(e);
}

export function getLogicalUserAgent() : UAParser.IResult | null {
  return LOGICAL_USER_AGENT;
}

export function isWebAndroid() {
  return AppUtilities.isWeb() && LOGICAL_USER_AGENT?.os?.name?.startsWith("Android");
}

export function getCreatorBulletItems(useBold: boolean) : BulletItem[] {
  return [
    { content: "Free to create a Rabbl. No monthly fees.", bold: useBold && true },
    { content: "You get " + AppConsts.CREATOR_CUT + " of the cover charges.", bold: useBold && true },
    { content: "You can choose to let some number of the first people in for free." },
    { content: "You can make it invite-only." },
    { content: "Create collabs with other Rabbls so your members see each other." },
  ]
};

export const CREATOR_BULLETS_BOLD = getCreatorBulletItems(true);
export const CREATOR_BULLETS_NO_BOLD = getCreatorBulletItems(false);

export const PASSWORD_REQUIREMENTS_BLANK : React.ReactNode = (
  <>
    <View style={{ width: AppConstants.STYLES.imageInfoSmall.width }}></View>
    <View style={{ width: AppConstants.STYLES.imageKeySmall.width }}></View>
  </>
);

export const SHOW_PASSWORD_BLANK : React.ReactNode = (
  <>
    <View style={{ width: AppConstants.STYLES.imageKeySmall.width }}></View>
  </>
);

export function getAgeYears(year: string, month: string, day: string): number {
  if (!isBlank(year) && !isBlank(month) && !isBlank(day)) {
    const evaluatedDay = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    const ageInYears = differenceInYears(new Date(), evaluatedDay);
    return ageInYears;
  } else {
    return Number.NaN;
  }
}

export const RANGE_SLIDER_THUMB = <Panel style={AppConsts.STYLES.rangeSliderThumb} pointerEvents='none' />;

export function RangeSliderThumb() { return RANGE_SLIDER_THUMB; }

export function getInstagramLink(str: string) : string {
  if (str.startsWith("http")) {
    return str;
  } else if (str.startsWith("www.") || str.startsWith("instagram.com")) {
    return "https://" + str;
  } else if (str.startsWith("@")) {
    return "https://www.instagram.com/" + str.substring(1);
  } else {
    return "https://www.instagram.com/" + str;
  }
}

export function getTikTokLink(str: string) : string {
  if (str.startsWith("http")) {
    return str;
  } else if (str.startsWith("www.") || str.startsWith("tiktok.com")) {
    return "https://" + str;
  } else if (str.startsWith("@")) {
    return "https://www.tiktok.com/" + str;
  } else {
    return "https://www.tiktok.com/@" + str;
  }
}

export function getSnapchatLink(str: string) : string {
  if (str.startsWith("http")) {
    return str;
  } else if (str.startsWith("www.") || str.startsWith("snapchat.com")) {
    return "https://" + str;
  } else if (str.startsWith("@")) {
    return "https://www.snapchat.com/add/" + str.substring(1);
  } else {
    return "https://www.snapchat.com/add/" + str;
  }
}

export function getFacebookLink(str: string) : string {
  if (str.startsWith("http")) {
    return str;
  } else if (str.startsWith("www.") || str.startsWith("facebook.com")) {
    return "https://" + str;
  } else if (str.startsWith("@")) {
    return "https://www.facebook.com/" + str.substring(1);
  } else {
    return "https://www.facebook.com/" + str;
  }
}

export function getXLink(str: string) : string {
  if (str.startsWith("http")) {
    return str;
  } else if (str.startsWith("www.") || str.startsWith("x.com")) {
    return "https://" + str;
  } else if (str.startsWith("@")) {
    return "https://x.com/" + str.substring(1);
  } else {
    return "https://x.com/" + str;
  }
}

export function getWebsiteLink(str: string) : string {
  if (str.startsWith("http")) {
    return str;
  } else {
    return "https://" + str;
  }
}
